import { useQuery } from '@tanstack/react-query'
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore'
import { COLLECTIONS, CSIU_STALENESS_WARNING_DAYS } from '@budgetmonkey/shared'
import { useCurrentUser } from '@/auth/AuthProvider'
import { db } from '@/lib/firebase'
import { toIso } from '@/lib/firestore'

/**
 * When each budget code was last covered by a CSIU import — §7.5 step 5.
 *
 * The dashboard and the code detail screen both ask "how stale is this?", and
 * the honest answer needs the import history rather than a guess from the
 * transactions themselves: an import that matched everything creates no new
 * rows, so the newest transaction on a code can be months older than the last
 * time it was actually reconciled.
 */
export interface CodeFreshness {
  lastImportedAt: string | null
  daysSince: number | null
  stale: boolean
}

export function useCsiuFreshness() {
  const user = useCurrentUser()

  return useQuery({
    queryKey: ['csiuFreshness', user.uid],
    queryFn: async () => {
      const snapshot = await getDocs(
        query(
          collection(db, COLLECTIONS.csiuImports),
          where('userId', '==', user.uid),
          orderBy('importedAt', 'desc'),
        ),
      )

      // Newest first, so the first import touching a code wins and later
      // (older) ones are ignored.
      const byCode = new Map<string, string>()
      for (const doc of snapshot.docs) {
        const importedAt = toIso(doc.get('importedAt'))
        if (importedAt === null) continue
        for (const codeId of (doc.get('budgetCodesTouched') as string[]) ?? []) {
          if (!byCode.has(codeId)) byCode.set(codeId, importedAt)
        }
      }
      return byCode
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function freshnessFor(
  byCode: ReadonlyMap<string, string> | undefined,
  codeId: string | undefined,
  now = Date.now(),
): CodeFreshness {
  const lastImportedAt = codeId === undefined ? undefined : byCode?.get(codeId)
  if (lastImportedAt === undefined) {
    return { lastImportedAt: null, daysSince: null, stale: false }
  }

  const daysSince = Math.floor(
    (now - new Date(lastImportedAt).getTime()) / 86_400_000,
  )
  return {
    lastImportedAt,
    daysSince,
    stale: daysSince > CSIU_STALENESS_WARNING_DAYS,
  }
}
